import _ from "lodash";
import { observer } from "mobx-react-lite";
import moment from "moment";
import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import Error404 from "../../components/Error404";
import MainContainer from "../../components/MainContainer";
import * as ArticleController from "../../controllers/article-controller";
import * as TagController from "../../controllers/tag-controller";
import MarkupStore from "../../store/MarkupStore";
import { DATE_FORMAT_UTC } from "../../utils/consts";
import { Item } from "./Item";

interface ITagListProps {
  tag: Nullable<TagResponse>;
  serverData: Nullable<ArticleFilterResponse>;
}

const PAGINATION_SIZE = 18;

function TagList({ tag, serverData }: ITagListProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const pageYOffsetRef = useRef(0);
  const currentPage = useMemo(() => +(router.query.page || 1), [router.query]);
  const { tagsMenuOpen, toggleTagsMenuOpen } = MarkupStore;

  const [list, setList] = useState<ArticleResponse[]>(serverData?.list || []);
  const [total, setTotal] = useState(serverData?.total || 0);
  const [loading, setLoading] = useState(false);

  const setPage = (page: number) => {
    router.push(
      {
        pathname: "/tag/[tagName]",
        query: {
          page: page,
          tagName: tag?.tagslug,
        },
      },
      undefined,
      { shallow: true }
    );
  };

  const fetchData = async () => {
    setLoading(true);
    const response = await ArticleController.getArticlesByFilter({
      filter: {
        tagslug: tag?.tagslug,
        is_draft: false,
        end_date: moment(new Date()).format(DATE_FORMAT_UTC),
      },
      start: currentPage * PAGINATION_SIZE,
      total: PAGINATION_SIZE,
    });
    if (response) {
      pageYOffsetRef.current = window.pageYOffset;
      setList([...list, ...response.list]);
      setTotal(response.total);
    }
    setLoading(false);
  };

  const handleLoadButtonClick = async () => {
    setPage(currentPage + 1);
    fetchData();
  };

  useLayoutEffect(() => {
    window.scroll({ top: pageYOffsetRef.current });
  }, [list]);

  useEffect(() => {
    pageYOffsetRef.current = 0;
    setList(serverData?.list || []);
    setTotal(serverData?.total || 0);
  }, [serverData]);

  if (!tag) {
    return <Error404 />;
  }

  const showLoadButton = total > list.length;

  return (
    <MainContainer
      ogTitle={tag?.seo_title}
      ogDescription={tag?.seo_description}
    >
      <div className="main-columns tags-page">
        <div className="main-columns__center">
          <div className="wrap">
            <div
              className={`tags-page__title-block${
                tagsMenuOpen ? " tags-menu-is-open" : ""
              }`}
            >
              <h1 className="tags-page__title">{tag?.tag}</h1>
              <button
                className="btn btn-fond size-xs light-gray tags-page__title-btn"
                onClick={() => toggleTagsMenuOpen(!tagsMenuOpen)}
              >
                <span className="icon icon-arrow-btn btn__icon" />
                <span className="btn__text">{t("Tags")}</span>
              </button>
            </div>

            {list.length === 0 ? (
              <div style={{ paddingBottom: 50 }}>{t("NoRecordsByTag")}</div>
            ) : (
              list.map((card) => <Item key={card.article_id} {...card} />)
            )}

            {showLoadButton && (
              <button
                className="btn btn-outline outline-dark tags-page__more-btn"
                onClick={handleLoadButtonClick}
              >
                <span className="btn__text">
                  {!loading ? t("ShowMore") : t("Loading")}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </MainContainer>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  params,
  res,
  query,
}): Promise<{ props: ITagListProps }> => {
  const translationsProps = await serverSideTranslations("ru");

  const tagName = params?.tagName as string;

  const startIndex = query.page ? +query.page - 1 : 0;
  const [tag, serverData] = await Promise.all([
    TagController.getTagByIndex(tagName),
    ArticleController.getArticlesByFilter({
      filter: {
        tagslug: tagName,
        is_draft: false,
        end_date: moment(new Date()).format(DATE_FORMAT_UTC),
      },
      start: startIndex * PAGINATION_SIZE,
      total: PAGINATION_SIZE,
    }),
  ]);

  if (!tag) {
    res.statusCode = 404;
  }

  return {
    props: {
      tag,
      serverData,
      ...translationsProps,
    },
  };
};

export default observer(TagList);
